import React, {
  useCallback, useState,
  useMemo, useEffect,
} from 'react';
import { useDispatch } from 'react-redux';

import { gtmEventHandler } from '@common/utils/gtm';
import { useMediaQuery } from '@common/hooks';
import { Button } from '@vectord/ui';

import { updateRequestParams } from '@alumni_v2/stores/reviews/actions';
import { getPreSelectedFilters } from '@alumni_v2/utils/getPreSelectedFilters';
import {
  sortedDropdownOptions,
  createAdvancedFiltersQueryDesktop,
  targetCompanyOptions,
} from '@alumni_v2/utils/filters';
import SelectField from '@alumni_v2/ui/SelectField';

import styles from './AdvancedFilters.module.scss';

const AdvancedFilters = ({
  advancedFilters,
  setAdvancedFilters,
}) => {
  const dispatch = useDispatch();
  const { mobile } = useMediaQuery();
  const [focusId, setFocusId] = useState(null);
  const { filterOptions } = window.__INITIAL_STATE__;

  const preScalerRoleOptions = useMemo(
    () => sortedDropdownOptions(
      filterOptions.pre_scaler_role, ['Student/Fresher', 'Non Tech'],
    ),
    [filterOptions.pre_scaler_role],
  );

  const targetRoleOptions = useMemo(
    () => sortedDropdownOptions(filterOptions.role, ['SDE1']),
    [filterOptions.role],
  );

  const preScalerCompanyOptions = useMemo(
    () => sortedDropdownOptions(filterOptions.pre_academy_company, ['TCS']),
    [filterOptions.pre_academy_company],
  );

  const collegeOptions = useMemo(
    () => sortedDropdownOptions(
      filterOptions.college, ['VIT Vellore', 'BITS Pilani'],
    ),
    [filterOptions.college],
  );

  const searchAlumniHandler = useCallback(() => {
    const { preSelectedFiltersJson, sharedFilter } = getPreSelectedFilters();

    const query = createAdvancedFiltersQueryDesktop(
      advancedFilters, sharedFilter,
    );

    gtmEventHandler(
      'cta',
      'apply_filter',
      'click',
      'alumni_directory',
      {
        click_type: 'apply_filter',
        click_section: 'filter_section',
        click_text: 'Search Alumni',
        gtm_custom_attributes: {
          current_role: advancedFilters.currentStatus?.label || null,
          current_company: advancedFilters.currentCompany?.label || null,
          target_role: advancedFilters.targetRole?.label || null,
          target_company: advancedFilters.targetCompany?.label || null,
          college: advancedFilters.currentCollege?.label || null,
        },
      },
    );

    dispatch(updateRequestParams(
      preSelectedFiltersJson,
      query,
      null,
      true,
      true,
    ));
  }, [advancedFilters, dispatch]);

  const updateFilters = useCallback((value, id) => {
    setAdvancedFilters((prevState) => ({
      ...prevState,
      [id]: value,
      ...(id === 'currentStatus' && {
        currentCompany: null,
        currentCollege: null,
      }),
    }));
  }, [setAdvancedFilters]);

  const initializeFilters = useCallback(() => {
    const { sharedFilter } = getPreSelectedFilters();

    setAdvancedFilters({
      currentStatus: sharedFilter.pre_scaler_role_matches_any?.[0]
        ? {
          value: sharedFilter.pre_scaler_role_matches_any[0],
          label: sharedFilter.pre_scaler_role_matches_any[0],
        }
        : null,
      currentCompany: sharedFilter.pre_academy_company_matches_any?.[0]
        ? {
          value: sharedFilter.pre_academy_company_matches_any[0],
          label: sharedFilter.pre_academy_company_matches_any[0],
        }
        : null,
      targetRole: sharedFilter.role_matches_any?.[0]
        ? {
          value: sharedFilter.role_matches_any[0],
          label: sharedFilter.role_matches_any[0],
        }
        : null,
      targetCompany: sharedFilter.post_academy_segment_matches_any?.[0]
        ? {
          value: sharedFilter.post_academy_segment_matches_any[0],
          label: sharedFilter.post_academy_segment_matches_any[0],
        }
        : null,
      currentCollege: sharedFilter.college_matches_any?.[0]
        ? {
          value: sharedFilter.college_matches_any[0],
          label: sharedFilter.college_matches_any[0],
        }
        : null,
    });
  }, [setAdvancedFilters]);

  useEffect(() => {
    initializeFilters();
  }, [initializeFilters]);

  if (mobile) {
    return null;
  }

  return (
    <div className={styles.mainContainer}>
      <div className={styles.fieldWrapper}>
        <SelectField
          id="currentStatus"
          containerClass={styles.fieldSelectWrapper}
          title="Current Status"
          options={[
            { value: 'Student/Fresher', label: 'Student/Fresher' },
            { value: 'WORK ROLE', label: '', isDisabled: true },
            { value: 'Non Tech', label: 'Non Tech' },
            {
              value: 'Other Engineering Role',
              label: 'Other Engineering Role',
            },
            ...preScalerRoleOptions,
          ]}
          selectedOption={advancedFilters}
          setSelectedOption={(value) => updateFilters(value, 'currentStatus')}
          focusId={focusId}
          setFocusId={setFocusId}
        />
        {advancedFilters?.currentStatus?.label === 'Student/Fresher'
         || advancedFilters?.currentStatus?.label === 'Student'
          ? (
            <>
              <SelectField
                id="currentCollege"
                containerClass={styles.fieldSelectWrapper}
                title=""
                options={[
                  { value: 'VIT Vellore', label: 'VIT Vellore' },
                  { value: 'BITS Pilani', label: 'BITS Pilani' },
                  { value: 'WORK ROLE', label: '', isDisabled: true },
                  ...collegeOptions,
                ]}
                placeholderText="Add College"
                placeholderActiveText="| Search College"
                selectedOption={advancedFilters}
                setSelectedOption={
                  (value) => updateFilters(value, 'currentCollege')
                }
                focusId={focusId}
                setFocusId={setFocusId}
              />
            </>
          ) : (
            <SelectField
              id="currentCompany"
              containerClass={styles.fieldSelectWrapper}
              title=""
              options={[
                { value: 'TCS', label: 'TCS' },
                { value: 'WORK ROLE', label: '', isDisabled: true },
                ...preScalerCompanyOptions,
              ]}
              placeholderText="Add Company"
              placeholderActiveText="| Search Company"
              selectedOption={advancedFilters}
              setSelectedOption={
                (value) => updateFilters(value, 'currentCompany')
              }
              focusId={focusId}
              setFocusId={setFocusId}
            />
          )}
        <div className={styles.dividerLine} />
        <SelectField
          id="targetRole"
          containerClass={styles.fieldSelectWrapper}
          title="Your Target"
          options={[
            { value: 'SDE1', label: 'SDE1' },
            { value: 'WORK ROLE', label: '', isDisabled: true },
            ...targetRoleOptions,
          ]}
          selectedOption={advancedFilters}
          setSelectedOption={
            (value) => updateFilters(value, 'targetRole')
          }
          focusId={focusId}
          setFocusId={setFocusId}
        />
        <SelectField
          id="targetCompany"
          containerClass={styles.fieldSelectWrapper}
          title=""
          options={[
            {
              value: 'Google, Amazon, Microsoft',
              label: 'Tech Giants',
            },
            { value: 'WORK ROLE', label: '', isDisabled: true },
            ...targetCompanyOptions,
          ]}
          placeholderText="Add Company"
          placeholderActiveText="| Search Company"
          selectedOption={advancedFilters}
          setSelectedOption={
            (value) => updateFilters(value, 'targetCompany')
          }
          focusId={focusId}
          setFocusId={setFocusId}
        />
        <Button
          variants="brand"
          buttonType="solid"
          title="SEARCH ALUMNI"
          size="l"
          weight="bold"
          spaced
          upperCase
          onClick={searchAlumniHandler}
        />
      </div>
    </div>
  );
};

export default AdvancedFilters;
